import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Building2, FileText, LogOut, LayoutDashboard, FolderOpen, Bell, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppLayout = ({ children }) => {
  const { user, logout, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingDocsCount, setPendingDocsCount] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Fetch orders with documents_pending status
  useEffect(() => {
    const fetchPendingDocs = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/orders?status=documents_pending&limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingDocsCount(response.data.orders?.length || 0);
        // Use pending docs count as notification indicator
        setUnreadNotifications(response.data.orders?.length || 0);
      } catch (err) {
        console.error('Error fetching pending docs count:', err);
      }
    };
    
    fetchPendingDocs();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingDocs, 30000);
    return () => clearInterval(interval);
  }, [token, location.pathname]); // Re-fetch when navigating

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/dashboard/companies', label: 'Companies', icon: Building2 },
    { path: '/dashboard/orders', label: 'Orders', icon: FileText },
    { path: '/dashboard/documents', label: 'Documents', icon: FolderOpen },
    { path: '/dashboard/docvault', label: 'DocVault', icon: ShieldCheck },
    { path: '/dashboard/data-room', label: 'Data Room', icon: FileText },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0b1310' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50"
        style={{ 
          backgroundColor: '#0b1310',
          borderBottom: '1px solid #1f2937',
          height: '60px'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 h-full flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <img 
              src="https://customer-assets.emergentagent.com/job_3e66b0ed-93a2-4dc7-abca-ce9b0d15b317/artifacts/wfxjz77g_StartEase%20Logo.png" 
              alt="StartEase Logo" 
              className="w-8 h-8 object-contain"
            />
            <span className="text-lg font-bold text-white tracking-tight">StartEase</span>
          </Link>
          
          {/* Right: User info + Notifications + Logout */}
          <div className="flex items-center gap-4">
            {/* User email */}
            <span className="text-xs text-gray-500 hidden sm:block">{user?.email}</span>
            
            {/* Notification bell */}
            <button
              onClick={() => navigate('/dashboard/orders')}
              className="relative p-2 text-gray-400 hover:text-white hover:bg-[#111827] rounded-lg transition-colors"
              data-testid="notification-bell"
              title="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </button>
            
            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-500 hover:text-white hover:bg-[#111827] rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 border-r border-gray-800 min-h-[calc(100vh-65px)] hidden md:block" style={{ backgroundColor: '#0b1310' }}>
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const showBadge = item.path === '/dashboard/orders' && pendingDocsCount > 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative ${
                    active 
                      ? 'bg-[#059669]/10 text-[#059669]' 
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                  {showBadge && (
                    <span className="absolute right-3 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" data-testid="orders-pending-badge" />
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Mobile Bottom Nav */}
        <nav className="fixed bottom-0 left-0 right-0 border-t border-gray-800 md:hidden z-50" style={{ backgroundColor: '#0b1310' }}>
          <div className="flex justify-around py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const showBadge = item.path === '/dashboard/orders' && pendingDocsCount > 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center gap-1 px-4 py-2 relative ${
                    active ? 'text-[#059669]' : 'text-gray-400'
                  }`}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5" />
                    {showBadge && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </div>
                  <span className="text-xs">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 p-6 pb-24 md:pb-6">
          <div className="max-w-6xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
